import { Check, ChevronDown, CircleAlert, ShieldAlert } from "lucide-react";
import type { AuditEvent } from "../../shared/audit-schemas";
import { AuditRequestDetailsSchema } from "../../shared/audit-request";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

export type RecordAuditEvent = AuditEvent & { appInstanceId: string; subjectKey: string };

function readable(value: string) {
  const words = value.replaceAll(/[._-]+/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const outcomes = {
  success: { label: "Succeeded", icon: Check, variant: "secondary" },
  denied: { label: "Denied", icon: ShieldAlert, variant: "outline" },
  error: { label: "Failed", icon: CircleAlert, variant: "destructive" },
} as const;

function requestSummary(event: AuditEvent) {
  const request =
    event.event === "app.request.completed"
      ? AuditRequestDetailsSchema.safeParse(event.payload)
      : null;
  if (request?.success && request.data.action && request.data.resourceType) {
    const { action, resourceType, recordId, actionName } = request.data;
    const title = `${readable(actionName ?? action)} ${resourceType}${recordId ? ` ${recordId}` : ""}`;
    return { title, anonymousActor: "Unauthenticated" };
  }
  const title = request?.success
    ? `Tool ${request.data.kind === "api" ? "API" : readable(request.data.kind).toLowerCase()} request · ${request.data.method} · HTTP ${request.data.status}`
    : readable(event.event);
  return { title, anonymousActor: request ? "Unauthenticated" : "System" };
}

export function AuditEventRow({
  event,
  showCustomer,
  onRecordHistory,
}: {
  event: AuditEvent;
  showCustomer: boolean;
  onRecordHistory?: ((event: RecordAuditEvent) => void) | undefined;
}) {
  const outcome = outcomes[event.outcome];
  const Icon = outcome.icon;
  const date = new Date(event.createdAt * 1000);
  const { title, anonymousActor } = requestSummary(event);
  const detail = [
    ["Event", event.event],
    ["Event ID", event.id],
    ["Request ID", event.requestId],
    ...(event.subjectKey ? [["Record", event.subjectKey]] : []),
    ...(event.appInstanceId ? [["Tool ID", event.appInstanceId]] : []),
    ...Object.entries(event.payload).map(([key, value]) => [
      readable(key),
      value === null ? "—" : String(value),
    ]),
  ];
  return (
    <details className="group border-b last:border-b-0 open:bg-muted/30">
      <summary className="grid cursor-pointer list-none grid-cols-[minmax(0,1fr)_auto] gap-3 px-4 py-4 outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset md:grid-cols-[minmax(0,1fr)_9rem_7rem_1rem] md:items-center [&::-webkit-details-marker]:hidden">
        <div className="min-w-0">
          <span className="block text-sm font-semibold break-words">{title}</span>
          <span className="mt-1 block text-xs text-muted-foreground break-words">
            {event.actorDisplayName ?? (event.actorUserId ? "Former user" : anonymousActor)}
            {event.actorUsername ? ` · ${event.actorUsername}` : ""}
            {event.appName ? ` · ${event.appName}` : ""}
            {showCustomer
              ? ` · ${event.customerName ?? (event.organizationId ? "Former customer" : "Platform")}`
              : ""}
          </span>
        </div>
        <time
          dateTime={date.toISOString()}
          className="col-start-1 row-start-2 text-xs text-muted-foreground tabular-nums md:col-auto md:row-auto"
        >
          {date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
          <span className="ml-2 md:mt-1 md:ml-0 md:block">
            {date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
          </span>
        </time>
        <Badge
          variant={outcome.variant}
          className="col-start-2 row-start-1 md:col-auto md:row-auto"
        >
          <Icon aria-hidden="true" />
          {outcome.label}
        </Badge>
        <ChevronDown
          aria-hidden="true"
          className="col-start-2 row-start-2 size-4 justify-self-end text-muted-foreground transition-transform group-open:rotate-180 motion-reduce:transition-none md:col-auto md:row-auto"
        />
      </summary>
      <RecordHistory event={event} onRecordHistory={onRecordHistory} />
      <dl className="grid gap-3 border-t border-dashed px-4 py-4 text-xs sm:grid-cols-2">
        {detail.map(([key, value], index) => (
          <div key={`${key}:${index}`} className="min-w-0">
            <dt className="mb-1 text-muted-foreground">{key}</dt>
            <dd className="m-0 font-mono break-words whitespace-pre-wrap">{value}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

function RecordHistory({
  event,
  onRecordHistory,
}: {
  event: AuditEvent;
  onRecordHistory: ((event: RecordAuditEvent) => void) | undefined;
}) {
  if (!event.subjectKey || !event.appInstanceId || !onRecordHistory) return null;
  const record = { ...event, appInstanceId: event.appInstanceId, subjectKey: event.subjectKey };
  return (
    <div className="border-t px-4 pt-3">
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          onRecordHistory(record);
        }}
      >
        View record history
      </Button>
    </div>
  );
}
